/**
 * Shared password validation utilities for frontend
 * Must match backend validation in lambda/shared/password-validation.ts
 */

export interface PasswordValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Password requirements:
 * - Minimum 12 characters
 * - Maximum 128 characters
 * - At least one letter (uppercase or lowercase)
 * - At least one number
 */
export function validatePassword(password: string): PasswordValidationResult {
  const errors: string[] = [];

  if (password.length < 12) {
    errors.push('Password must be at least 12 characters');
  }

  if (password.length > 128) {
    errors.push('Password must be at most 128 characters');
  }

  if (!/[a-zA-Z]/.test(password)) {
    errors.push('Password must contain at least one letter');
  }

  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Human-readable password requirements for display in UI
 */
export const PASSWORD_REQUIREMENTS = [
  'At least 12 characters',
  'At least one letter (a-z or A-Z)',
  'At least one number (0-9)',
];

/**
 * Check individual password requirements for UI feedback
 */
export function checkPasswordRequirements(password: string): {
  minLength: boolean;
  hasLetter: boolean;
  hasNumber: boolean;
} {
  return {
    minLength: password.length >= 12,
    hasLetter: /[a-zA-Z]/.test(password),
    hasNumber: /[0-9]/.test(password),
  };
}
